import { useQuery } from '@tanstack/react-query';
import { CircleAlert, ExternalLink, LayoutGrid } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { useToast } from '../components/Toast';
import { useAuth } from '../features/auth/AuthProvider';
import { gridLabels, layoutsKey, listLayouts, type Layout } from '../features/live/layouts';
import { openMosaicWindow } from '../features/live/mosaicWindow';
import { useTimeFormat } from '../lib/useTimeFormat';

export function MosaicsPage() {
  const { request } = useAuth();
  const { notify } = useToast();
  const fmt = useTimeFormat();
  const layouts = useQuery({ queryKey: layoutsKey, queryFn: () => listLayouts(request) });
  const items = [...(layouts.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  const open = (l: Layout) => {
    if (!openMosaicWindow(l.id)) {
      notify('O navegador bloqueou a janela do mosaico. Permita janelas pop-up para este site.');
    }
  };

  return (
    <>
      <PageHeader
        title="Mosaicos"
        description="Clique no nome para abrir o mosaico numa janela só com as câmeras."
      />
      <div className="p-4 sm:p-6">
        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {layouts.isError && !layouts.data ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar os mosaicos"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void layouts.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : layouts.data?.length === 0 ? (
            <EmptyState
              icon={LayoutGrid}
              title="Nenhum mosaico salvo"
              description="Monte um mosaico na tela Ao vivo e salve-o: ele aparece aqui para abrir numa janela própria."
              action={
                <Link
                  to="/live"
                  className="inline-flex h-8 items-center rounded-md bg-accent-strong px-3 text-[13px] font-medium text-white hover:bg-accent"
                >
                  Ir para o Ao vivo
                </Link>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full min-w-[640px] text-left text-[13px]"
                aria-busy={layouts.isFetching}
              >
                <caption className="sr-only">Mosaicos salvos</caption>
                <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                  <tr className="border-b border-line">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Mosaico
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Grade
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Câmeras
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Visibilidade
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Atualizado
                    </th>
                    <th scope="col" className="w-px px-4 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!layouts.data
                    ? Array.from({ length: 4 }, (_, i) => (
                        <tr
                          key={i}
                          aria-hidden="true"
                          className="border-b border-line last:border-0"
                        >
                          {['w-48', 'w-10', 'ml-auto w-6', 'w-20', 'w-24', 'w-16'].map((w) => (
                            <td key={w} className="px-4 py-3.5">
                              <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : items.map((l) => (
                        <tr
                          key={l.id}
                          className="border-b border-line last:border-0 hover:bg-surface-hover/50"
                        >
                          <td className="px-4 py-2.5">
                            <button
                              type="button"
                              onClick={() => {
                                open(l);
                              }}
                              className="inline-flex items-center gap-1.5 text-left font-medium text-accent hover:underline"
                            >
                              {l.name}
                              <ExternalLink aria-hidden="true" className="size-3.5" />
                              <span className="sr-only">(abre em nova janela)</span>
                            </button>
                            <div className="text-xs text-fg-subtle">{l.owner.display_name}</div>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-fg-muted">
                            {gridLabels[l.grid]}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                            {l.tiles.length}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-fg-muted">
                            {l.shared ? 'Compartilhado com a empresa' : 'Pessoal'}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-fg-muted">
                            {fmt.absolute(l.updated_at)}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <Link
                              to={`/live?layout=${encodeURIComponent(l.id)}`}
                              className="inline-flex h-7 items-center rounded-md px-3 text-[13px] font-medium whitespace-nowrap text-fg-muted hover:bg-surface-hover hover:text-fg"
                            >
                              Abrir no Ao vivo
                            </Link>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
